import React from 'react'
import { TouchableOpacity, View } from 'react-native'
import { Button, Icon } from '@src/components/base'
import { NotificationContent } from '@src/components/notifications/content/NotificationContent'
import { useNotificationInboxPresentation } from '@src/components/notifications/hooks/useNotificationInboxPresentation'
import { NotificationRecord } from '@src/models/notifications/types'
import { StyleService, useStyleSheet } from '@src/style/service'
import { NotificationEngineNotificationActionKind } from '@src/types'

interface NotificationInboxRowProps {
  isFirst: boolean
  isLast: boolean
  item: NotificationRecord
  onPress: (notification: NotificationRecord) => void
  timestamp: string
}

export const NotificationInboxRow = ({
  item,
  isFirst,
  isLast,
  onPress,
  timestamp,
}: NotificationInboxRowProps) => {
  const styles = useStyleSheet(themedStyles)
  const presentation = useNotificationInboxPresentation({ notification: item })
  const showActionButton = item.actionKind !== NotificationEngineNotificationActionKind.None

  return (
    <View
      style={[styles.row, isFirst && styles.rowFirst, isLast ? styles.rowLast : styles.rowMiddle]}
    >
      <View>
        <TouchableOpacity
          accessibilityLabel={item.content}
          activeOpacity={0.8}
          onPress={() => onPress(item)}
          style={[styles.pressable, showActionButton && styles.pressableWithAction]}
        >
          <View style={[styles.content, showActionButton && styles.contentWithAction]}>
            <NotificationContent
              notification={item}
              presentation={presentation}
              showTimestamp
              showUnreadDot
              timestamp={timestamp}
              useReadStateColors
            />
          </View>

          {showActionButton && (
            <View
              style={styles.chevronColumn}
              testID={`NotificationInbox/ChevronContainer/${item.id}`}
            >
              <Icon name="caret-right" size={16} weight="bold" style={styles.chevron} />
            </View>
          )}
        </TouchableOpacity>

        {showActionButton && (
          <View style={styles.actionButtonContainer}>
            <Button
              accessibilityLabel={item.actionButtonLabel}
              onPress={() => onPress(item)}
              size="tiny"
              style={styles.actionButton}
              type="primary"
            >
              {item.actionButtonLabel}
            </Button>
          </View>
        )}
      </View>
      {!isLast && <View style={styles.rowDivider} />}
    </View>
  )
}

const themedStyles = StyleService.create({
  row: {
    backgroundColor: 'theme.background',
    borderColor: 'theme.surface.base1',
    borderLeftWidth: 1,
    borderRightWidth: 1,
    overflow: 'hidden',
  },
  rowFirst: {
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderTopWidth: 1,
  },
  rowLast: {
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    borderBottomWidth: 1,
  },
  rowMiddle: {
    borderBottomWidth: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
    paddingRight: 20,
  },
  contentWithAction: {
    paddingRight: 0,
  },
  rowDivider: {
    height: 1,
    marginLeft: 68,
    backgroundColor: 'theme.surface.base1',
  },
  pressable: {
    flexDirection: 'row',
    gap: 4,
    paddingBottom: 16,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  pressableWithAction: {
    paddingBottom: 6,
  },
  actionButtonContainer: {
    paddingBottom: 16,
    paddingLeft: 68,
    paddingRight: 16,
  },
  actionButton: {
    alignSelf: 'flex-start',
  },
  chevronColumn: {
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  chevron: {
    color: 'theme.text.tertiary',
  },
})
